import './ImagesEditor.scss';
import * as React from 'react';
import { Notify } from '@shared/ui-native/Notify';
import { setMapCursor } from '@openlayers/ol-utils';
import { Ajax } from '@shared/Ajax';
import { mapRootStore, mapTools, mapUI, mapWrapper } from '../map';
import { FormErrors, FormGroup } from '@shared/ui-react/UtilComponents';
import { Events } from '@shared/Events';
import { Drawer } from '@shared/ui-native/Drawer';
import { cs } from '@shared/utils/utils-classname';
import { AreaSelection } from './shared/AreaSelection';
import { transform as olTransform } from 'ol/proj';
import { unByKey } from 'ol/Observable';
import { ImagesService } from '@shared/images/ImagesService';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { ClickSelection } from '@pages/map/actions/shared/ClickSelection';
import { SelectionModePicker } from '@pages/map/actions/shared/SelectionModePicker';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { DocumentsImagesStoreAbstract } from '@shared/documents-images/DocumentsImagesStoreAbstract';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { FileTreeService } from '@shared/services/FileTreeService';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { ProjectPropertyJsonArrayStore } from '@pages/project-settings/shared/ProjectPropertyJsonArrayStore';
import { DialogUtils } from '@shared/ui-react/Dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from '@shared/ui-react/Button';
import { SelectionMapMarkers } from '@openlayers/SelectionMapMarkers';
import { TabPane, Tabs } from '@shared/ui-react/Tabs';
import { DocumentsImagesPanelThumbnails } from '@shared/documents-images/ui/DocumentsImagesPanelThumbnails';
import { PixelPackerWindow } from '@shared/images/pixelpacker/PixelPackerWindow';

type SelectMode = 'click' | 'area';

export const ImagesEditor = {
    open(images: IImage[] = [], store: DocumentsImagesStoreAbstract<IImage>, activateEditLocation = false) {
        if (!kuura.hasPrivilege('IMAGES_W')) return;

        const drawerElement = Drawer.open();
        Events.trigger('drawer-open.editor');

        ReactUtils.safeUnmountComponentAtNode(drawerElement);
        ReactUtils.safeRender(<ImageEditSelectablePanel images={images} store={store} activateEditLocation={activateEditLocation} />, drawerElement);
    },

    openForId: (id: number, store: DocumentsImagesStoreAbstract<IImage>) => {
        const found = store.getById(id);
        if (found) {
            ImagesEditor.open([found], store);
        }
    },

    close() {
        Drawer.close();
    },
};

@observer
export class ImageEditSelectablePanel extends React.Component<{
    images: IImage[];
    store: DocumentsImagesStoreAbstract<IImage>;
    activateEditLocation?: boolean;
}> {
    @observable selectMode: SelectMode = 'area';
    @observable.ref images = this.props.images || [];

    private areaSelection: AreaSelection<IImage>;
    private clickSelection: ClickSelection<IImage>;

    constructor(props: { images: IImage[]; store: DocumentsImagesStoreAbstract<IImage> }) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.clickSelection = new ClickSelection(mapWrapper.featureLayers.images, selection => this.onImagesSelected(selection, false), null);
        this.areaSelection = new AreaSelection(mapWrapper.featureLayers.images.layerSource, selection => this.onImagesSelected(selection, true));
        if (this.props.images.length === 0) {
            this.setSelectionMode(this.selectMode);
        }
    }

    componentWillUnmount(): void {
        this.clickSelection.deactivate();
        this.areaSelection.deactivate();
        mapTools.deactivateAllTools();
    }

    render() {
        const images = this.images;

        // js_map.draw_polygon_to_select_images
        if (!images || !Array.isArray(images) || images.length === 0) {
            return <SelectionModePicker value={this.selectMode} onChange={mode => this.setSelectionMode(mode)} onClose={ImagesEditor.close} />;
        }

        return (
            <ImageEditPanel
                images={this.images}
                onSave={this.onSave}
                onDelete={this.onDelete}
                selectionTool={this.selectMode === 'area' ? this.areaSelection : this.clickSelection}
                activateEditLocation={this.props.activateEditLocation}
            />
        );
    }

    onDelete = (imageIds: number[]) => {
        const promise = mapRootStore.imagesStore.removeByIds(imageIds);
        if (promise) {
            promise.done(() => {
                ImagesEditor.close();
            });
        }
    };

    onSave = (imageUuids: string[] = [], newValues: any) => {
        if (newValues.hasOwnProperty('lat') && newValues.hasOwnProperty('lon')) {
            newValues.geographicPoint = {
                lat: newValues.lat === '' ? 0 : newValues.lat,
                lon: newValues.lon === '' ? 0 : newValues.lon,
            };
        }

        if (newValues.hasOwnProperty('alignmentId')) {
            const node = FileTreeService.findNodeById(mapRootStore.treeData, newValues.alignmentId);
            if (node) {
                newValues.alignmentName = node.name;
            }
        }

        this.props.store.mergeItems(imageUuids, newValues);
        mapWrapper.featureLayers.images.redraw();
        const updatedItems = this.props.store.getByUuids(imageUuids);
        Notify.success();
        setTimeout(() => ImagesEditor.open(updatedItems, this.props.store), 10);
    };

    setSelectionMode = (selectMode: SelectMode) => {
        if (selectMode == 'click') {
            mapTools.activateTool(this.clickSelection);
        } else if (selectMode == 'area') {
            mapTools.activateTool(this.areaSelection);
        }
        this.selectMode = selectMode;
    };

    onImagesSelected = (selection: IImage[], clear: boolean) => {
        this.images = selection;
    };
}

@observer
class ImageEditPanel extends React.Component<{
    images: IImage[];
    onSave: (imageUuids: string[], newValues) => void;
    onDelete: (imageIds) => void;
    selectionTool: any;
    activateEditLocation?: boolean;
}> {
    state = {
        direction: 0,
        lat: 0,
        lon: 0,
        n: 0, // just for display
        e: 0, // just for display
        alignmentId: -1,
        positionMode: false,
        locationFormGroupChecked: false,
    };

    @observable activeTemplate: string = null;
    @observable isLoading = false;
    @observable isDownloading = false;
    @observable bulkNameChecked = false;
    @observable checkedIds = [];

    nameTemplatesStore = new ProjectPropertyJsonArrayStore<INamingTemplate>(ImagesService.nameTemplatesStorageKey);

    constructor(props) {
        super(props);
        makeObservable(this);

        this.isLoading = true;
        this.nameTemplatesStore.fetch().always(() => {
            if (this.nameTemplatesStore.allItems.length === 0) {
                this.nameTemplatesStore.allItems.push({
                    // per spec, have a default template when no templates exist
                    t: ImagesService.defaultNameTemplate,
                });
            }
            this.isLoading = false;
        });

        this.checkedIds = this.props.images.map(item => item.id);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.images !== this.props.images) {
            this.checkedIds = this.props.images.map(item => item.id);
        }
    }

    form: HTMLFormElement;

    private mapMarkers: SelectionMapMarkers;
    private destinationClickHandler = null;

    handleLocationSelection = (e?: any) => {
        if (e?.currentTarget?.disabled) return; // prevent clicking on icon of disabled button

        this.setState({
            positionMode: true,
        });

        setMapCursor(mapWrapper.getInstance(), 'crosshair');
        mapTools.activeTool = 'image-select';
        if (this.props.selectionTool) {
            this.props.selectionTool.deactivate();
        }

        this.destinationClickHandler = mapWrapper.events.onClickOnce(e => {
            setMapCursor(mapWrapper.getInstance(), '');
            if (this.props.selectionTool) {
                mapTools.activateTool(this.props.selectionTool);
            }
            const coords = e.coordinate;
            const [lon, lat] = olTransform(coords, 'EPSG:3857', 'EPSG:4326');

            this.setState(
                {
                    positionMode: false,
                    lat,
                    lon,
                },
                () => {
                    this.fetchNEDebounced();
                }
            );

            this.mapMarkers.setDestinationLinesTarget(lat, lon);
        });
    };

    cancelLocationSelection = e => {
        setMapCursor(mapWrapper.getInstance(), '');
        mapTools.activeTool = null;
        unByKey(this.destinationClickHandler);

        this.setState({
            positionMode: false,
        });
    };

    handleLatChange = lat => {
        this.setState({
            lat: isNaN(lat) ? '' : lat,
        });
        this.fetchNEDebounced();
    };

    handleLonChange = lon => {
        this.setState({
            lon: isNaN(lon) ? '' : lon,
        });
        this.fetchNEDebounced();
    };

    handleAngleChange = e => {
        this.setState({
            direction: e.currentTarget.value,
        });
    };

    locationFormGroupCheck = (isChecked: boolean) => {
        this.setState({
            locationFormGroupChecked: isChecked,
        });

        this.mapMarkers.showDestinationLines(isChecked);
    };

    getPhotoRenamedName = (i: IImage, formData: Record<string, string>) => {
        const modelSelected = mapUI.mapPanels.panels.models.tree.getSelected();
        const modelName = modelSelected && !modelSelected.isFolder() ? modelSelected.ajaxData?.name : null;
        const folderName = FileTreeService.findNodeById(mapRootStore.treeData, formData.folderId ?? i.folderId)?.name ?? null;
        const description = formData.description ?? i.description;
        const alignment = {
            alignmentName: FileTreeService.findNodeById(mapRootStore.treeData, formData.alignmentId ?? i.alignmentId)?.name ?? null,
            station: i.station,
        };
        const bValue = null; // no geolocation on desktop
        const latLon = [formData.lon ?? i.geographicPoint?.lon, formData.lat ?? i.geographicPoint?.lat] as [number, number];

        return ImagesService.renameImage(
            this.activeTemplate,
            i.name,
            folderName,
            description,
            i.originalDate ? new Date(i.originalDate) : null,
            latLon,
            modelName,
            bValue,
            alignment,
            i.creator?.username
        );
    };

    save = e => {
        const imageIds = this.checkedIds;

        const formData: any = {
            imageIds,
        };

        $(this.form)
            .serializeArray()
            .forEach(field => {
                // serializeArray will ignore disabled fields
                formData[field.name] = field.value;
            });

        if (formData.name_template) {
            formData.names = imageIds.map(id => {
                return this.getPhotoRenamedName(
                    this.props.images.find(i => i.id === id),
                    formData
                );
            });
        }
        if (!this.isBulk() || (this.isBulk() && this.state.locationFormGroupChecked)) {
            formData.lon = this.state.lon;
            formData.lat = this.state.lat;
        }
        console.log('formData:', formData);

        this.isLoading = true;
        Ajax({
            url: 'images_bulk_edit.json',
            method: 'POST',
            data: formData,
        })
            .always(() => {
                this.isLoading = false;
            })
            .fail(Notify.error)
            .done(resp => this.props.onSave(resp.updatedUuids, formData));
    };

    delete = () => {
        this.props.onDelete(this.checkedIds);
    };

    download = (annotated = false) => {
        if (annotated) {
            const uuids = this.checkedIds.map(id => this.props.images.find(i => i.id === id).uuid);
            PixelPackerWindow.open(uuids, true);
        } else {
            this.setState({ isDownloading: true });
            ImagesService.download(this.checkedIds, null).always(() => this.setState({ isDownloading: false }));
        }
    };

    render() {
        const { direction, lat, lon, positionMode } = this.state;
        const { images } = this.props;
        const isBulk = this.isBulk();
        const firstImage = this.props.images[0];

        let hasTooLongName = false;
        if (this.activeTemplate && this.bulkNameChecked) {
            const formData: any = {};
            $(this.form)
                .serializeArray()
                .forEach(field => (formData[field.name] = field.value));
            hasTooLongName = this.props.images.some(i => this.getPhotoRenamedName(i, formData).length > 255);
        }

        return (
            <div>
                <header style={this.isBulk() ? { height: 110, marginBottom: -43 } : { height: 150 }}>
                    {isBulk ? (
                        <div className='padded'>
                            <strong className='flex-row' style={{ justifyContent: 'space-between' }}>
                                <span>{__('js_map.bulk_edit')}</span>{' '}
                                <span>
                                    {kuura.getMessage('general.selected_x')}: <span className='badge badge-blue'>{this.checkedIds.length}</span>
                                </span>
                            </strong>
                        </div>
                    ) : (
                        <img src={kuura.getContextPath() + '/api/1/image/' + images[0].id + '_m.jpg'} className='responsive' />
                    )}
                </header>

                <Tabs displayValue='flex' flexDirection='column' hideSingleTab={true}>
                    <TabPane title={__('general.edit')}>
                        <form onSubmit={e => e.preventDefault()} ref={e => (this.form = e)} className={cs({ 'overlay-loading': this.isLoading })}>
                            <FormGroup title={kuura.getMessage('general.name')} checkable={isBulk} checked={this.bulkNameChecked} onChecked={e => (this.bulkNameChecked = e)}>
                                <div>
                                    <div className='flex-row'>
                                        {this.activeTemplate ? (
                                            <input type='text' name='name_template' value={this.activeTemplate} readOnly style={{ background: 'transparent' }} key='template' />
                                        ) : (
                                            <input type='text' name='name' defaultValue={firstImage.name} maxLength={255} key='manual-name' />
                                        )}

                                        <Dropdown closeOnBodyClick={true} className='ik-button-secondary--subtle ik-button-small' tooltip={__('js_name_templates.template')}>
                                            <span>
                                                <i className='iconf-caret' />
                                            </span>
                                            <ul className='clickable'>
                                                <li onClick={() => (this.activeTemplate = null)} style={{ borderBottom: '2px solid #ccc' }}>
                                                    <input type='checkbox' checked={this.activeTemplate === null} />
                                                    <span>{__('js_photo.name.enter_value')}</span>
                                                </li>
                                                {this.nameTemplatesStore.allItems.map((t, i) => (
                                                    <li onClick={() => (this.activeTemplate = t.t)} key={i}>
                                                        <input type='checkbox' checked={this.activeTemplate === t.t} />
                                                        <span>{t.n?.length > 0 ? t.n : t.t}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </Dropdown>
                                    </div>
                                    {this.activeTemplate && (
                                        <button onClick={this.previewNewNames} className='ik-button-small ik-button-secondary--subtle'>
                                            {__('js_photo.name.rename_preview')}
                                        </button>
                                    )}
                                    {hasTooLongName && <FormErrors error={{ type: 'maxLength', maxLength: 255, message: __('js_photo.name.names_too_long') }} />}
                                </div>
                            </FormGroup>

                            <FormGroup title={kuura.getMessage('general.description')} checkable={isBulk}>
                                <textarea name='description' defaultValue={firstImage.description}></textarea>
                            </FormGroup>

                            <FormGroup title={kuura.getMessage('general.folder')} checkable={isBulk}>
                                <FiletreeSelectReact
                                    formFieldName='folderId'
                                    selectable='folder'
                                    treeData={mapRootStore.treeData}
                                    selectedId={firstImage.folderId}
                                    storageKey={'ImagesEditor_folderSelectTree'}
                                />
                            </FormGroup>

                            <FormGroup title={kuura.getMessage('general.alignment')} checkable={isBulk}>
                                <FiletreeSelectReact
                                    formFieldName='alignmentId'
                                    selectable='file'
                                    treeData={mapRootStore.treeData}
                                    selectedId={firstImage.alignmentId}
                                    hideEmptyFolders={true}
                                    canClearValue={true}
                                    childFilter={this.filetreeFilterAlignments}
                                    storageKey={'ImagesEditor_alignmentSelectTree'}
                                />
                            </FormGroup>

                            <FormGroup
                                title={kuura.getMessage('general.location')}
                                checkable={isBulk}
                                checked={this.state.locationFormGroupChecked}
                                onChecked={this.locationFormGroupCheck}
                            >
                                <div id='image-edit-position-form'>
                                    <div>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td className='text-right'>lat:</td>
                                                    <td>
                                                        <InputNumber
                                                            onValueChange={e => this.handleLatChange(e.value)}
                                                            value={lat}
                                                            disabled={isBulk ? !this.state.locationFormGroupChecked : false}
                                                            min={-90}
                                                            max={90}
                                                            locale='en-US'
                                                            inputStyle={{ width: '100%' }}
                                                            mode='decimal'
                                                            useGrouping={false}
                                                            minFractionDigits={0}
                                                            maxFractionDigits={12}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className='text-right'>lon:</td>
                                                    <td>
                                                        <InputNumber
                                                            onValueChange={e => this.handleLonChange(e.value)}
                                                            value={lon}
                                                            disabled={isBulk ? !this.state.locationFormGroupChecked : false}
                                                            min={-180}
                                                            max={180}
                                                            locale='en-US'
                                                            inputStyle={{ width: '100%' }}
                                                            mode='decimal'
                                                            useGrouping={false}
                                                            minFractionDigits={0}
                                                            maxFractionDigits={12}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className='text-right'>n:</td>
                                                    <td style={{ paddingLeft: 15 }}>{this.state.n}</td>
                                                </tr>
                                                <tr>
                                                    <td className='text-right'>e:</td>
                                                    <td style={{ paddingLeft: 15 }}>{this.state.e}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    {!this.state.positionMode ? (
                                        <div>
                                            <button
                                                className='js-location-btn break-text ik-button-secondary'
                                                style={{ flexDirection: 'column', width: '100%' }}
                                                onClick={this.handleLocationSelection}
                                            >
                                                <i className='iki iki-map-pin' style={{ margin: '0 0 0 0', fontSize: 16 }}></i>
                                                <span>{kuura.getMessage('js_images.select_location')}</span>
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            {kuura.getMessage('js_images.select_location_click')} |{' '}
                                            <a onClick={this.cancelLocationSelection}>{kuura.getMessage('general.cancel')}</a>
                                        </div>
                                    )}
                                </div>
                            </FormGroup>

                            <FormGroup title={kuura.getMessage('js_images.direction_degrees')} checkable={isBulk}>
                                <div className='flex-row'>
                                    {utils.isIE ? null : (
                                        <input type='range' min={-1} max={360} name='direction' value={direction} onChange={this.handleAngleChange} required={true} />
                                    )}
                                    <input
                                        type='number'
                                        min={-1}
                                        max={360}
                                        style={{ width: 70, marginLeft: 10 }}
                                        value={direction}
                                        onChange={this.handleAngleChange}
                                        required={true}
                                    />
                                    <span className='image-direction-arrow' style={{ transform: 'rotate(' + direction + 'deg)', verticalAlign: 'top' }}></span>
                                </div>
                            </FormGroup>

                            <footer>
                                <Button small onClick={this.delete} disabled={this.checkedIds.length === 0} className='btn-danger' title={kuura.getMessage('general.delete')}>
                                    <i className='iki iki-trash'></i>
                                </Button>

                                {kuura.hasPrivilege('Download_Image') && (
                                    <Dropdown disabled={this.isDownloading || this.checkedIds.length === 0} closeOnBodyClick>
                                        <i className='iki iki-download'></i>
                                        <ul className='clickable'>
                                            <li onClick={() => this.download(false)}>
                                                <i className='iki iki-download'></i> {__('general.download')}
                                            </li>
                                            <li onClick={() => this.download(true)}>
                                                <i className='iki iki-download'></i> {__('js_images.download_annotated_images')} (BETA)
                                            </li>
                                        </ul>
                                    </Dropdown>
                                )}

                                <div style={{ float: 'right' }}>
                                    <Button secondary subtle onClick={ImagesEditor.close} type='button'>
                                        {kuura.getMessage('general.close')}
                                    </Button>
                                    <Button primary onClick={this.save} disabled={this.isLoading || hasTooLongName || this.checkedIds.length === 0}>
                                        {kuura.getMessage('general.save')}
                                    </Button>
                                </div>
                            </footer>
                        </form>
                    </TabPane>
                    {isBulk && (
                        <TabPane title={__('general.browse_selection')}>
                            <div className='flex-column' style={{ padding: '10px 5px' }}>
                                <DocumentsImagesPanelThumbnails
                                    type='images'
                                    items={this.props.images}
                                    unfilteredItems={this.props.images}
                                    checkedIds={this.checkedIds}
                                    onChecked={ids => {
                                        this.checkedIds = ids;
                                        this.drawSelectionMarkers();
                                    }}
                                    canDownload={false}
                                    hasContextMenu={false}
                                    canEdit={false}
                                    canSelect={true}
                                    onClick={id =>
                                        mapWrapper.featureLayers.images.openImageById(
                                            id,
                                            this.props.images.map(i => i.id)
                                        )
                                    }
                                />
                            </div>
                        </TabPane>
                    )}
                </Tabs>
            </div>
        );
    }

    componentDidMount() {
        const firstImage = this.props.images[0];

        this.setState(
            {
                direction: firstImage.direction,
                lat: firstImage.geographicPoint?.lat || 0,
                lon: firstImage.geographicPoint?.lon || 0,
            },
            () => {
                this.mapMarkers = new SelectionMapMarkers(mapWrapper);
                this.drawSelectionMarkers();
                this.fetchNE();
            }
        );

        if (this.props.activateEditLocation) {
            this.handleLocationSelection();
            document.querySelector('#image-edit-position-form')?.classList.add('flash-background');
        }
    }

    componentWillUnmount() {
        unByKey(this.destinationClickHandler);
        this.mapMarkers.destroy();
    }

    componentWillReceiveProps() {
        setTimeout(this.drawSelectionMarkers);
    }

    isBulk() {
        return this.props.images && this.props.images.length > 1;
    }

    filetreeFilterAlignments(item) {
        if (item.tolerance) return true; // folder
        return kuuraModel.isAlignment(item);
    }

    drawSelectionMarkers = () => {
        this.mapMarkers.drawMarkersDestinationLines(this.checkedIds.map(id => this.props.images.find(img => img.id === id)));
        this.setLines();
        this.mapMarkers.showDestinationLines(!this.isBulk()); // in bulk mode, disable destination lines until location checkbox is checked
    };

    setLines = () => {
        if (this.state.lat && this.state.lon) {
            this.mapMarkers.setDestinationLinesTarget(this.state.lat, this.state.lon);
        } else {
            this.mapMarkers.showDestinationLines(false);
        }
    };

    previewNewNames = () => {
        const formData: any = {};
        $(this.form)
            .serializeArray()
            .forEach(field => {
                formData[field.name] = field.value;
            });

        DialogUtils.renderComponentBasic(
            __('js_photo.name.rename_preview'),
            <div style={{ maxWidth: '90vw' }}>
                <table className='styled-table striped' style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th>{kuura.getMessage('general.history.old_value')}</th>
                            <th>{kuura.getMessage('general.history.new_value')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.checkedIds.map(id => {
                            const image = this.props.images.find(img => img.id === id);
                            const newName = this.getPhotoRenamedName(image, formData);
                            return (
                                <tr key={image.id}>
                                    <td>{image.name}</td>
                                    <td style={{ background: newName?.length > 255 ? '#FFD0D0' : 'transparent' }}>{newName}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    fetchNE = () => {
        if (!this.state.lat || !this.state.lon) {
            this.setState({
                e: '',
                n: '',
            });
        } else {
            const lat = Math.max(-90, Math.min(this.state.lat, 90));
            const lon = Math.max(-180, Math.min(this.state.lon, 180));

            Ajax({
                url: 'api/1/project/' + kuura.getProjectId() + '/convert_lat_lon.json',
                data: {
                    lat,
                    lon,
                },
            }).done(resp => {
                this.setState({
                    e: resp.coords.e.toFixed(2),
                    n: resp.coords.n.toFixed(2),
                });
            });
        }
    };

    fetchNEDebounced = _.debounce(this.fetchNE, 200);
}
